import { Link, useParams } from "react-router"
import { ArrowLeft, Download, RotateCcw } from "lucide-react"
import { useMemo, useState } from "react"

import {
  type CreosUniqueJob,
  useCreosUniqueBatch,
  useRestartCreosUniqueJob,
} from "@/api/hooks/creos"
import { Button } from "@/components/ui/button"
import { usePageTitle } from "@/layouts/pageChrome"
import { triggerAuthenticatedDownload } from "@/lib/download"
import { cn } from "@/lib/utils"

const CAROUSEL_ACCENTS = [
  "border-l-[#3db8a0]",
  "border-l-[#d4a017]",
  "border-l-[#7488e8]",
  "border-l-[#e07a5f]",
  "border-l-[#81b29a]",
  "border-l-[#9b7ebd]",
] as const

function statusBadgeClass(status: string): string {
  switch (status) {
    case "completed":
      return "bg-emerald-500/20 text-emerald-300"
    case "failed":
      return "bg-red-500/20 text-red-300"
    case "partial":
      return "bg-orange-500/20 text-orange-300"
    case "processing":
      return "bg-amber-500/20 text-amber-300"
    case "pending":
      return "bg-sky-500/20 text-sky-300"
    default:
      return "bg-white/10 text-[#aaa]"
  }
}

function formatDuration(sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return null
  const s = Math.round(sec)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`
  const h = Math.floor(m / 60)
  const rm = m % 60
  return rm ? `${h}h ${rm}m` : `${h}h`
}

function processingLabel(job: CreosUniqueJob): string {
  if (job.status === "pending") return "Queued"
  const d = formatDuration(job.duration_sec)
  if (job.status === "processing") {
    return d ? `Processing ${d}` : "Processing…"
  }
  if (d) return d
  return "—"
}

function jobDownloadName(job: CreosUniqueJob): string {
  if (job.media_kind === "carousel" || job.file_count > 1) {
    return `creos-unique-${job.public_id}.zip`
  }
  const first = job.s3_urls?.[0]?.filename
  return first || `creos-unique-${job.public_id}`
}

function JobActions({
  job,
  restartPending,
  restartId,
  onRestart,
}: {
  job: CreosUniqueJob
  restartPending: boolean
  restartId?: string
  onRestart: (id: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {job.status === "completed" && job.download_url ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-[#555] bg-transparent text-white hover:bg-[#333]"
          onClick={() => {
            triggerAuthenticatedDownload(job.download_url!, jobDownloadName(job))
          }}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {job.media_kind === "carousel" || job.file_count > 1
            ? "ZIP"
            : "Download"}
        </Button>
      ) : null}
      {job.status === "failed" ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={restartPending && restartId === job.public_id}
          className="border-[#555] bg-transparent text-white hover:bg-[#333]"
          onClick={() => onRestart(job.public_id)}
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {restartPending && restartId === job.public_id
            ? "Restarting…"
            : "Restart"}
        </Button>
      ) : null}
    </div>
  )
}

function VideoJobCard({
  job,
  restartPending,
  restartId,
  onRestart,
}: {
  job: CreosUniqueJob
  restartPending: boolean
  restartId?: string
  onRestart: (id: string) => void
}) {
  const preview = job.s3_urls?.[0]
  const sourceName = job.source_filenames[0] || ""
  // Match carousel slide naming: originalStem-publicId.ext
  const title =
    preview?.filename ||
    (sourceName
      ? `${sourceName.replace(/\.[^.]+$/, "")}-${job.public_id}${
          sourceName.match(/\.[^.]+$/)?.[0] || ".mp4"
        }`
      : job.public_id)

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-[#4a4a4a] bg-[#2d2d2d]">
      <div className="flex items-center justify-between gap-2 border-b border-[#3a3a3a] px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-white" title={title}>
            {title}
          </p>
          <Link
            to={`/creos/uniquify/${job.public_id}`}
            className="font-mono text-xs text-[#8b9aff] hover:underline"
          >
            {job.public_id}
          </Link>
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize",
            statusBadgeClass(job.status),
          )}
        >
          {job.status}
        </span>
      </div>

      <div className="relative bg-black">
        {job.status === "completed" &&
        preview?.content_type.startsWith("video/") ? (
          <video
            src={preview.url}
            controls
            playsInline
            preload="metadata"
            className="aspect-[9/16] max-h-[320px] w-full object-contain"
          />
        ) : job.status === "completed" && preview ? (
          <img
            src={preview.url}
            alt={preview.filename}
            className="aspect-[9/16] max-h-[320px] w-full object-contain"
          />
        ) : (
          <div className="flex aspect-[9/16] max-h-[320px] items-center justify-center text-xs text-[#666]">
            {job.status === "pending"
              ? "Waiting…"
              : job.status === "processing"
                ? "Encoding…"
                : job.status === "failed"
                  ? "Failed"
                  : "No preview"}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span
          className="text-xs text-[#aaa]"
          title="Time spent in Processing (excludes queue wait)"
        >
          {processingLabel(job)}
        </span>
        <JobActions
          job={job}
          restartPending={restartPending}
          restartId={restartId}
          onRestart={onRestart}
        />
      </div>
      {job.error_message ? (
        <p className="border-t border-[#3a3a3a] px-3 py-2 text-xs text-red-300">
          {job.error_message}
        </p>
      ) : null}
    </div>
  )
}

function CarouselJobCard({
  job,
  index,
  restartPending,
  restartId,
  onRestart,
}: {
  job: CreosUniqueJob
  index: number
  restartPending: boolean
  restartId?: string
  onRestart: (id: string) => void
}) {
  const accent = CAROUSEL_ACCENTS[index % CAROUSEL_ACCENTS.length]
  const slideCount = job.s3_urls?.length || job.file_count

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-[#4a4a4a] border-l-4 bg-[#2d2d2d]",
        accent,
      )}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-[#3a3a3a] px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-white">
              Carousel {index + 1}
              <span className="ml-1.5 font-normal text-[#888]">
                · {slideCount} slide{slideCount === 1 ? "" : "s"}
              </span>
            </p>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-medium capitalize",
                statusBadgeClass(job.status),
              )}
            >
              {job.status}
            </span>
            <span
              className="text-xs text-[#aaa]"
              title="Time spent in Processing (excludes queue wait)"
            >
              {processingLabel(job)}
            </span>
          </div>
          <Link
            to={`/creos/uniquify/${job.public_id}`}
            className="font-mono text-xs text-[#8b9aff] hover:underline"
          >
            {job.public_id}
          </Link>
        </div>
        <JobActions
          job={job}
          restartPending={restartPending}
          restartId={restartId}
          onRestart={onRestart}
        />
      </div>

      {job.error_message ? (
        <p className="px-4 py-2 text-sm text-red-300">{job.error_message}</p>
      ) : null}

      {job.status === "completed" && job.s3_urls?.length ? (
        <div className="flex gap-3 overflow-x-auto p-4">
          {job.s3_urls.map((item, idx) => (
            <div
              key={item.key || idx}
              className="relative w-[120px] shrink-0 overflow-hidden rounded-lg border border-[#3a3a3a] bg-[#242424]"
            >
              <span className="absolute left-1.5 top-1.5 z-10 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">
                #{idx + 1}
              </span>
              <a href={item.url} target="_blank" rel="noreferrer">
                <img
                  src={item.url}
                  alt={item.filename}
                  className="h-[200px] w-full object-contain bg-black"
                />
              </a>
              <button
                type="button"
                className="flex w-full items-center justify-center gap-1 border-t border-[#3a3a3a] py-1.5 text-[11px] text-[#ccc] hover:bg-[#333] hover:text-white"
                onClick={() => {
                  triggerAuthenticatedDownload(item.download_url, item.filename)
                }}
              >
                <Download className="h-3 w-3" aria-hidden />
                Download
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 py-6 text-sm text-[#666]">
          {job.status === "pending"
            ? "Waiting in queue…"
            : job.status === "processing"
              ? "Processing slides…"
              : job.status === "failed"
                ? "No slides"
                : "No slides yet"}
        </p>
      )}
    </div>
  )
}

export default function CreosUniquifyBatchDetailPage() {
  const params = useParams()
  const batchId = (params.batchId || "").trim()
  const short = batchId.replace(/-/g, "").slice(0, 8)
  usePageTitle("", short ? `Uniquify batch ${short}` : "CreOS")
  const [restartError, setRestartError] = useState<string | null>(null)
  const restart = useRestartCreosUniqueJob()

  const { data: batch, isLoading, error } = useCreosUniqueBatch(batchId, {
    enabled: Boolean(batchId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === "pending" ||
        status === "processing" ||
        status === "partial"
        ? 5000
        : false
    },
  })

  const { videos, carousels } = useMemo(() => {
    const jobs = batch?.jobs ?? []
    return {
      videos: jobs.filter((j) => j.media_kind === "video"),
      carousels: jobs.filter((j) => j.media_kind === "carousel"),
    }
  }, [batch?.jobs])

  function handleRestart(publicId: string) {
    setRestartError(null)
    restart.mutate(publicId, {
      onError: (err) => {
        setRestartError(
          err instanceof Error ? err.message : "Failed to restart",
        )
      },
    })
  }

  const batchZipReady =
    Boolean(batch?.batch_id) &&
    (batch?.status === "completed" || batch?.status === "partial") &&
    Boolean(batch?.download_url) &&
    (batch?.completed_count ?? 0) > 0

  const mixParts: string[] = []
  if (videos.length) {
    mixParts.push(
      `${videos.length} video${videos.length === 1 ? "" : "s"}`,
    )
  }
  if (carousels.length) {
    mixParts.push(
      `${carousels.length} carousel${carousels.length === 1 ? "" : "s"}`,
    )
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          asChild
          variant="outline"
          className="border-[#555] bg-transparent text-white hover:bg-[#333]"
        >
          <Link to="/creos/uniquify">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
            Back
          </Link>
        </Button>
      </div>

      {restartError ? (
        <p className="text-sm text-red-300">{restartError}</p>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-[#aaa]">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-300">{error.message}</p>
      ) : !batch ? (
        <p className="text-sm text-[#aaa]">Batch not found</p>
      ) : (
        <>
          <div className="w-full rounded-xl border border-[#4a4a4a] bg-[#2d2d2d] px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-mono text-xl font-semibold text-white">
                    {short || batchId}
                  </h1>
                  <span
                    className={cn(
                      "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                      statusBadgeClass(batch.status),
                    )}
                  >
                    {batch.status}
                  </span>
                  {batch.job_count > 1 ? (
                    <span className="text-sm text-[#aaa]">
                      {batch.completed_count}/{batch.job_count} done
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-[#aaa]">
                  {mixParts.join(" · ") || "—"}
                  {batch.duration_sec != null ? (
                    <>
                      {" · "}
                      total elapsed {formatDuration(batch.duration_sec)}
                    </>
                  ) : null}
                </p>
              </div>
              {batch.batch_id ? (
                <Button
                  type="button"
                  aria-disabled={!batchZipReady}
                  title={batchZipReady ? undefined : "Wait until finished"}
                  className={cn(
                    "shrink-0 bg-[#7488e8] text-white hover:bg-[#687bda]",
                    !batchZipReady &&
                      "cursor-not-allowed bg-[#3a3a3a] text-[#888] hover:bg-[#3a3a3a]",
                  )}
                  onClick={() => {
                    if (!batchZipReady || !batch.download_url) return
                    triggerAuthenticatedDownload(
                      batch.download_url,
                      `creos-batch-${short}.zip`,
                    )
                  }}
                >
                  <Download className="mr-2 h-4 w-4" aria-hidden />
                  Batch ZIP
                </Button>
              ) : null}
            </div>
          </div>

          {batch.status === "pending" || batch.status === "processing" ? (
            <p className="text-sm text-[#aaa]">
              Processing… this page refreshes automatically.
            </p>
          ) : null}

          {videos.length ? (
            <section className="space-y-3">
              <h2 className="text-base font-semibold text-white">
                Videos
                <span className="ml-2 text-sm font-normal text-[#888]">
                  {videos.length}
                </span>
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {videos.map((job) => (
                  <VideoJobCard
                    key={job.public_id}
                    job={job}
                    restartPending={restart.isPending}
                    restartId={restart.variables}
                    onRestart={handleRestart}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {carousels.length ? (
            <section className="space-y-3">
              <h2 className="text-base font-semibold text-white">
                Carousels
                <span className="ml-2 text-sm font-normal text-[#888]">
                  {carousels.length}
                </span>
              </h2>
              <div className="space-y-4">
                {carousels.map((job, idx) => (
                  <CarouselJobCard
                    key={job.public_id}
                    job={job}
                    index={idx}
                    restartPending={restart.isPending}
                    restartId={restart.variables}
                    onRestart={handleRestart}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {!videos.length && !carousels.length ? (
            <p className="text-sm text-[#aaa]">No items in this batch.</p>
          ) : null}
        </>
      )}
    </div>
  )
}
